import { useEffect, useState } from 'react';
import { Trans } from '@lingui/macro';
import { Moon, Settings } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';

/**
 * 테마 모드 타입
 * - 'auto': 시스템 설정(prefers-color-scheme)을 따름
 * - 'light': 항상 라이트모드 강제
 */
export type ThemeMode = 'auto' | 'light';

const THEME_KEY = 'theme-preference';
const DARK_INTRO_KEY = 'dark-mode-intro-shown';

/** 프로필 모달 열기 이벤트 */
export const OPEN_PROFILE_EVENT = 'open-profile-modal';

/** 현재 저장된 테마 설정 읽기 (기본값: auto) */
export function getThemePreference(): ThemeMode {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light') return 'light';
    return 'auto';
}

/** 테마 설정 저장 */
export function setThemePreference(mode: ThemeMode) {
    localStorage.setItem(THEME_KEY, mode);
    applyTheme(mode);
}

/** 실제 DOM에 다크 클래스를 적용/제거 */
function applyTheme(mode: ThemeMode) {
    if (mode === 'light') {
        document.documentElement.classList.remove('dark');
    } else {
        // auto → 시스템 설정에 따름
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDark) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }
}

// ⚡ 모듈 로드 시 즉시 테마 적용 (React 마운트 전에 dark 클래스 설정)
// → OTA 업데이트 모달, 스플래시 화면 등 초기 렌더링부터 다크모드 반영
applyTheme(getThemePreference());

/** 다크모드 안내 다이얼로그를 표시해야 하는지 여부 */
function shouldShowDarkIntro(): boolean {
    return !localStorage.getItem(DARK_INTRO_KEY);
}

/** 다크모드 안내를 표시했다고 기록 */
function markDarkIntroShown() {
    localStorage.setItem(DARK_INTRO_KEY, 'true');
}

/**
 * ThemeManager - 앱 전체의 테마(다크모드) 관리 컴포넌트
 * 
 * App.tsx에 한 번만 마운트하면 됩니다.
 * - localStorage의 'theme-preference' 값에 따라 테마를 적용합니다.
 * - 'auto' 모드일 때 OS 시스템 설정 변경을 실시간 리스닝합니다.
 * - 처음 다크모드가 적용될 때 안내 다이얼로그를 표시합니다. (로그인 상태에서만)
 */
export function ThemeManager({ isLoggedIn = false }: { isLoggedIn?: boolean }) {
    const [showIntro, setShowIntro] = useState(false);

    useEffect(() => {
        const mode = getThemePreference();
        applyTheme(mode);

        // 처음으로 다크모드가 적용되었을 때 안내 다이얼로그 표시
        if (isLoggedIn && mode === 'auto' && shouldShowDarkIntro()) {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            if (prefersDark) {
                setShowIntro(true);
            }
        }

        // auto 모드일 때 시스템 설정 변경 감지
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => {
            const currentMode = getThemePreference();
            if (currentMode === 'auto') {
                applyTheme('auto');

                // 시스템 설정이 다크로 변경되었을 때, 처음이면 안내 표시
                if (isLoggedIn && mediaQuery.matches && shouldShowDarkIntro()) {
                    setShowIntro(true);
                }
            }
        };

        mediaQuery.addEventListener('change', handleChange);

        // 연속 키 입력을 위한 버퍼 상태
        let keysBuffer: string[] = [];
        const supportedLangs = ['ko', 'ja', 'en'];

        // 전역 단축키 핸들러
        const handleKeyDown = (e: KeyboardEvent) => {
            // Option + D (Alt + D) 다크모드 직접 토글
            if (e.altKey && e.code === 'KeyD') {
                e.preventDefault();
                document.documentElement.classList.toggle('dark');
                return;
            }

            // 디버깅/관리자용: 언어 로테이션 (Option 누른 상태에서 L 누르고 T 반복 입력)
            if (e.altKey) {
                if (e.code === 'KeyL') {
                    keysBuffer.push('KeyL');
                } else if (e.code === 'KeyT') {
                    const lastKey = keysBuffer.length > 0 ? keysBuffer[keysBuffer.length - 1] : null;
                    // 직전에 L을 눌렀거나, 이미 T를 눌러서 로테이션 중인 상태라면 진행
                    if (lastKey === 'KeyL' || lastKey === 'KeyT') {
                        e.preventDefault();
                        keysBuffer.push('KeyT'); // 연속 T 입력을 위해 버퍼 갱신
                        
                        import('../../services/My').then(({ My }) => {
                            import('../../services/FireClient').then(({ Fire }) => {
                                import('../../locales/InitTranslation').then(({ activateLanguage }) => {
                                    const currentLang = My.user.lang || 'ko';
                                    const nextIndex = (supportedLangs.indexOf(currentLang) + 1) % supportedLangs.length;
                                    const newLang = supportedLangs[nextIndex];
                                    
                                    My.user.lang = newLang;
                                    activateLanguage(newLang);
                                    
                                    if (My.isLoggedIn()) {
                                        Fire.saveCurrentUser(My.user);
                                    }
                                    
                                    console.log(`[Language Rotation] Changed to ${newLang} for debugging.`);
                                });
                            });
                        });
                    }
                } else {
                    // Option을 누른 상태에서 L이나 T가 아닌 다른 키를 누르면 취소
                    keysBuffer = [];
                }
            } else {
                // Option 키를 떼면 버퍼 초기화
                keysBuffer = [];
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'Alt' || !e.altKey) {
                keysBuffer = [];
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            mediaQuery.removeEventListener('change', handleChange);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [isLoggedIn]);

    const handleClose = () => {
        markDarkIntroShown();
        setShowIntro(false);
    };

    const handleOpenProfile = () => {
        markDarkIntroShown();
        setShowIntro(false);
        // 프로필 모달 열기 이벤트 발생
        window.dispatchEvent(new CustomEvent(OPEN_PROFILE_EVENT));
    };

    return (
        <Dialog open={showIntro} onOpenChange={(open) => { if (!open) handleClose(); }}>
            <DialogContent className="max-w-sm">
                <DialogHeader className="items-center text-center">
                    <div className="mx-auto mb-2 p-3 rounded-full bg-muted">
                        <Moon className="w-6 h-6 text-foreground" />
                    </div>
                    <DialogTitle>
                        <Trans>Dark mode is on</Trans>
                    </DialogTitle>
                    <DialogDescription className="text-center">
                        <Trans>Dark mode has been automatically applied based on your device settings. You can change this anytime in your profile settings.</Trans>
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-2 mt-2">
                    <Button onClick={handleOpenProfile} className="w-full">
                        <Settings className="w-4 h-4 mr-2" />
                        <Trans>Open Profile Settings</Trans>
                    </Button>
                    <Button variant="outline" onClick={handleClose} className="w-full">
                        <Trans>Close</Trans>
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
